import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Report from './pages/Report';
import Auth from './pages/Auth';
import GdprSettings from './pages/GdprSettings';
import AccessibilitySettingsPage from './pages/AccessibilitySettings';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Clock, LogOut, ShieldAlert } from 'lucide-react';

const InactiveUserScreen: React.FC = () => {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 font-sans">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-[32px] p-10 shadow-xl border border-slate-100 dark:border-slate-800 text-center animate-in fade-in zoom-in-95 duration-500">
        <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/10">
          <Clock size={40} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Pending Activation</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8">
          Your account has been created successfully. For security reasons, an administrator must review and activate your account before you can access the dashboard.
        </p>
        <div className="flex flex-col gap-3">
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700 flex items-center gap-3 text-left">
            <ShieldAlert size={20} className="text-indigo-500 shrink-0" />
            <p className="text-xs text-slate-500 dark:text-slate-400">Please contact <strong>mbui41@gmail.com</strong> for faster approval.</p>
          </div>
          <button 
            onClick={() => logout()}
            className="w-full py-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isActive, loading } = useAuth();
  
  if (loading) return (
    <div className="h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
    </div>
  );
  
  if (!user) return <Navigate to="/auth" replace />;
  if (!isActive) return <InactiveUserScreen />;
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/report/:id" element={
              <ProtectedRoute>
                <Report />
              </ProtectedRoute>
            } />
            
            <Route path="/report/:id/gdpr-settings" element={
              <ProtectedRoute>
                <GdprSettings />
              </ProtectedRoute>
            } />

            <Route path="/report/:id/accessibility" element={
              <ProtectedRoute>
                <AccessibilitySettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/public/:id" element={<Report readOnly={true} />} />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;